import { useAdminAccess } from "src/hooks/useAdminAccess";
import { isCurrentStoryBibleOwner } from "src/utils/storyBibleUpdateFromChunk";
import * as Sentry from "@sentry/react";
import { isSelectableLegacyVoiceModel } from "../../../../src/shared/legacyVoiceRetirement.js";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { normalizeErrorMessage } from "src/lib/Errors";
import { isProbableModelOutageError } from "src/lib/modelOutage";
import Cookies from "universal-cookie";
import { useImmer } from "use-immer";
import { MIN_DOC_TITLING_WORDS, MAX_SYNOPSIS_WORDS, MAX_BRAINDUMP_WORDS } from "../../../../src/shared/constants";
import LegacyOutline from "../../../../src/shared/Models/LegacyOutline";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema";
import { OUTLINE_TYPES } from "../../../../src/shared/storyEngineConstants";
import Auth from "../../Auth";
import { useProjectStore } from "../../stores/projectStore";
import { useSeriesStore } from "../../stores/seriesStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { countWords, makeId, sendInvocationEvent } from "../../Utils";
import Characters from "../Characters";
import ProjectUpgradeOffer from "../ProjectUpgradeOffer";
import { useToast } from "../ToastWrapper";
import Worldbuilding from "../Worldbuilding";
import AdminMenu from "./AdminMenu";
import ChapterCreatorBox from "./ChapterCreatorBox";
import CopyableTextarea from "./CopyableTextarea";
import HelpModal from "./HelpModal";
import History from "./History";
import useCollapsedSections from "./Hooks/useCollapsedSections";
import StoryBibleSectionInViewTracker from "./Hooks/StoryBibleSectionInViewTracker";
import usePersist from "./Hooks/usePersist";
import { useStoryBibleHistory } from "./Hooks/useStoryBibleHistory";
import { resolveInputToast } from "./handleTextareaInput";
import { resolveSaveErrorToast } from "./saveErrorToast";
import { resolveStyleGuideDraft } from "./styleGuideDraft";
import { useRefetchOnFocus } from "../../hooks/useRefetchOnFocus";
import OutlinePicker from "./OutlinePicker";
import StructuredOutline from "./StructuredOutline";
import CreateDocument, { CREATE_DOCUMENT_DISPLAY_MODES } from "./StructuredOutline/CreateDocument";
import { useStructuredOutlineStore } from "./StructuredOutline/StructuredOutlineStore";
import Style from "./Style";
import { StyledContainer } from "./styles";
import { orderedScenesBySectionAndScene } from "./Utils";
import { getAirTableSupportUrl } from "../HelpMenu";
import ModelOutageMessage from "../ModelOutageMessage";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../StickyTour/TourTypes";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { AppEventPublisher } from "../../AppEventPublisher";
import IconButton from "../IconButton";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isStoryBibleCtaBlockingRetrigger } from "src/components/PersistentChat/storyBibleCta";
import { useCrossAppSignalStore, getSynopsisCTASignal } from "src/stores/crossAppSignalStore";

const clientConversionSingletons = {};
const AUTOMATIC_TITLE_SECTIONS = ["seed", "synopsis"];

export const ENGINES = [
  { label: "Default", value: null },
  { label: "gpt-4o-mini-2024-07-18", value: "gpt-4o-mini-2024-07-18" },
  { label: "gpt-4-32k", value: "gpt-4-32k" },
];

export const FUNCTION_NAMES = {
  SEED: "seed",
  GENRE: "genre",
  SYNOPSIS: "synopsis",
  CHARACTERS: "characters",
  OUTLINE: "outline",
  SUMMARY: "summary",
  COMMANDS: "commands",
  SCENE_PROSE: "sceneProse",
  STYLE: "style",
  SCENES: "scenes",
};

// TODO: should we combine the two objects below into a single data structure?

export const LEGACY_STYLE_INPUT_LIMIT = 40;

export function getStyleInputLimit(hasAccessToStyleGuide = false) {
  return hasAccessToStyleGuide ? null : LEGACY_STYLE_INPUT_LIMIT;
}

export const INPUT_LIMITS = {
  [FUNCTION_NAMES.SEED]: MAX_BRAINDUMP_WORDS,
  [FUNCTION_NAMES.GENRE]: 40,
  [FUNCTION_NAMES.STYLE]: LEGACY_STYLE_INPUT_LIMIT,
  [FUNCTION_NAMES.SYNOPSIS]: MAX_SYNOPSIS_WORDS,
  [FUNCTION_NAMES.CHARACTERS]: 700,
  [FUNCTION_NAMES.OUTLINE]: 1700,
  [FUNCTION_NAMES.SUMMARY]: 150,
  [FUNCTION_NAMES.COMMANDS]: 2000,
  [FUNCTION_NAMES.SCENES]: 2000,
};

export const FUNCTION_ICONS = {
  [FUNCTION_NAMES.SEED]: "/assets/story-bible/seed-icon.svg",
  [FUNCTION_NAMES.GENRE]: "/assets/story-bible/genre-icon.svg",
  [FUNCTION_NAMES.STYLE]: "/assets/story-bible/style-icon.svg",
  [FUNCTION_NAMES.SYNOPSIS]: "/assets/story-bible/synopsis-icon.svg",
  [FUNCTION_NAMES.CHARACTERS]: "/assets/story-bible/characters-icon.svg",
  [FUNCTION_NAMES.OUTLINE]: "/assets/story-bible/outline-icon.svg",
};

export const PROSE_MODE_CATEGORIES = {
  LEGACY: "Legacy",
  STANDARD: "Standard",
  EXPERIMENTAL: "Experimental",
  CUSTOM: "Custom",
};

// Phase 1 standalone turtle candidates were removed when Phase 2 launched.
// Saved MUSE2_* values still fall back via proseMode; ledger labels are kept
// for provenance. Phase 2 is the MUSE_2_ALPHA comparison mode below.
const MUSE2_ALPHA_PROSE_MODES = {};

export const PROSE_MODES = {
  ...MUSE2_ALPHA_PROSE_MODES,
  MUSE_2_ALPHA: {
    key: "MUSE_2_ALPHA",
    value: "muse-2-alpha",
    label: "Muse 2 Alpha",
    subLabel: "Compare and rate new prose",
    iconPath: "/assets/model-selection-modal/muse_m.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    scenesEngine: "muse2-raphael",
    muse2Alpha: true,
    benchmarkMode: true,
  },
  BASIC: {
    key: "BASIC",
    value: "basic",
    label: "Basic",
    subLabel: "Good quality & economical",
    iconPath: "/assets/model-selection-modal/basic.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    scenesEngine: "gpt-4o-mini",
  },
  EXCELLENT: {
    key: "EXCELLENT",
    value: "excellent",
    label: "Excellent",
    subLabel: "Great for long scenes",
    iconPath: "/assets/model-selection-modal/excellent.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    scenesEngine: "ballad-1.1-draft",
  },
  BEST_PROSE: {
    key: "BEST_PROSE",
    value: "best-prose",
    label: "Best Prose (v1)",
    subLabel: "Faster, too!",
    iconPath: "/assets/awardRibbon.svg",
    category: PROSE_MODE_CATEGORIES.LEGACY,
  },

  FASTEST: {
    key: "FASTEST",
    value: "fastest",
    label: "Fastest",
    subLabel: "You might get dizzy",
    iconPath: "/assets/speedyArrow.svg",
    category: PROSE_MODE_CATEGORIES.LEGACY,
  },
  SPICY: {
    key: "SPICY",
    value: "spicy",
    label: "Unfiltered",
    subLabel: "Sex, violence, and horror.",
    iconPath: "/assets/pepper.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
  },
  o3: {
    key: "o3",
    value: "o3",
    label: "GPT o3",
    scenesEngine: "o3",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  },
  gemini: {
    key: "gemini",
    value: "gemini",
    label: "Gemini Pro 2.5",
    scenesEngine: "gemini",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  },
  GEMINI_2_5_FLASH_THINKING: {
    key: "GEMINI_2_5_FLASH_THINKING",
    value: "geminiFlashThinking",
    label: "Gemini 2.5 Flash Thinking",
    scenesEngine: "geminiFlashThinking",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  GEMINI_3_1_PRO: {
    key: "GEMINI_3_1_PRO",
    value: "gemini31Pro",
    label: "Gemini 3.1 Pro",
    scenesEngine: "gemini31Pro",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  GEMINI_3_FLASH: {
    key: "GEMINI_3_FLASH",
    value: "gemini3Flash",
    label: "Gemini 3 Flash",
    scenesEngine: "gemini3Flash",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  GEMINI_3_7_FLASH: {
    key: "GEMINI_3_7_FLASH",
    value: "gemini37Flash",
    label: "Gemini 3.7 Flash",
    scenesEngine: "gemini37Flash",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  GEMINI_3_8_FLASH: {
    key: "GEMINI_3_8_FLASH",
    value: "gemini38Flash",
    label: "Gemini 3.8 Flash",
    scenesEngine: "gemini38Flash",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  KIMI_K2: {
    key: "KIMI_K2",
    value: "kimiK2",
    label: "Kimi K2",
    scenesEngine: "kimiK2",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  GLM_5_2: {
    key: "GLM_5_2",
    value: "glm52",
    label: "GLM 5.2",
    scenesEngine: "glm52",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  KIMI_K3: {
    key: "KIMI_K3",
    value: "kimiK3",
    label: "Kimi K3",
    scenesEngine: "kimiK3",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  KIMI_K2_THINKING: {
    key: "KIMI_K2_THINKING",
    value: "kimiK2Thinking",
    label: "Kimi K2 Thinking",
    scenesEngine: "kimiK2Thinking",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  // Removed by OpenRouter
  // AIROBOROS_70B: {
  //   key: "AIROBOROS_70B",
  //   value: "openrouter",
  //   label: "Airoboros 70B",
  //   engine: "jondurbin/airoboros-l2-70b",
  //   category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  // },
  CLAUDE_3_OPUS: {
    key: "CLAUDE_3_OPUS",
    value: "claude-3-opus",
    label: "Premium",
    subLabel: "Faster, too!",
    iconPath: "/assets/awardRibbon.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    scenesEngine: "claude-3-opus",
  },
  // this technically goes to the same endpoint as CLAUDE_3_OPUS
  // we just give it the Opus label to differentiate it in the UI
  // we distinguish the source value via the /raw path, but this does not affect the functionality
  CLAUDE_3_OPUS_RAW: {
    key: "CLAUDE_3_OPUS_RAW",
    value: "claude-3-opus/raw",
    label: "Claude 3 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-3-opus",
  },
  CLAUDE_3_SONNET: {
    key: "CLAUDE_3_SONNET",
    value: "claude-3-sonnet",
    label: "Claude 3 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-3-sonnet",
  },
  CLAUDE_3_5_SONNET: {
    key: "CLAUDE_3_5_SONNET",
    value: "claude-3-5-sonnet",
    label: "Claude 3.5 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-3-5-sonnet",
  },
  CLAUDE_4_5_SONNET: {
    key: "CLAUDE_4_5_SONNET",
    value: "claude-4-5-sonnet",
    label: "Claude 4.5 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-5-sonnet",
  },
  CLAUDE_SONNET_4_6: {
    key: "CLAUDE_SONNET_4_6",
    value: "claude-4-6-sonnet",
    label: "Claude 4.6 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-6-sonnet",
  },
  CLAUDE_5_SONNET: {
    key: "CLAUDE_5_SONNET",
    value: "claude-5-sonnet",
    label: "Claude 5 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-sonnet",
    new: true,
  },
  CLAUDE_5_5_SONNET: {
    key: "CLAUDE_5_5_SONNET",
    value: "claude-5-5-sonnet",
    label: "Claude 5.5 Sonnet",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-5-sonnet",
    new: true,
  },
  CLAUDE_4_1_OPUS: {
    key: "CLAUDE_4_1_OPUS",
    value: "claude-4-1-opus",
    label: "Claude 4.1 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-1-opus",
  },
  CLAUDE_4_5_OPUS: {
    key: "CLAUDE_4_5_OPUS",
    value: "claude-4-5-opus",
    label: "Claude 4.5 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-5-opus",
  },
  CLAUDE_4_6_OPUS: {
    key: "CLAUDE_4_6_OPUS",
    value: "claude-4-6-opus",
    label: "Claude 4.6 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-6-opus",
  },
  CLAUDE_4_7_OPUS: {
    key: "CLAUDE_4_7_OPUS",
    value: "claude-4-7-opus",
    label: "Claude 4.7 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-7-opus",
  },
  CLAUDE_4_8_OPUS: {
    key: "CLAUDE_4_8_OPUS",
    value: "claude-4-8-opus",
    label: "Claude 4.8 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-8-opus",
  },
  CLAUDE_5_OPUS: {
    key: "CLAUDE_5_OPUS",
    value: "claude-5-opus",
    label: "Claude 5 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-opus",
    new: true,
  },
  CLAUDE_5_5_OPUS: {
    key: "CLAUDE_5_5_OPUS",
    value: "claude-5-5-opus",
    label: "Claude 5.5 Opus",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-5-opus",
    new: true,
  },
  CLAUDE_5_FABLE: {
    key: "CLAUDE_5_FABLE",
    value: "claude-5-fable",
    label: "Claude 5 Fable",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-fable",
  },
  CLAUDE_5_1_FABLE: {
    key: "CLAUDE_5_1_FABLE",
    value: "claude-5-1-fable",
    label: "Claude 5.1 Fable",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-1-fable",
    new: true,
  },
  CLAUDE_5_5_HAIKU: {
    key: "CLAUDE_5_5_HAIKU",
    value: "claude-5-5-haiku",
    label: "Claude 5.5 Haiku",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-5-5-haiku",
    new: true,
  },
  CLAUDE_4_5_HAIKU: {
    key: "CLAUDE_4_5_HAIKU",
    value: "claude-4-5-haiku",
    label: "Claude 4.5 Haiku",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "claude-4-5-haiku",
  },
  GPT_4_O: {
    key: "GPT_4_O",
    value: "gpt-4o",
    label: "GPT 4o",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-4o",
  },
  GPT_4_O_MINI: {
    key: "GPT_4_O_MINI",
    value: "gpt-4o-mini",
    label: "GPT 4o Mini",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-4o-mini",
  },
  GPT_4_1: {
    key: "GPT_4_1",
    value: "gpt-4.1",
    label: "GPT 4.1",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-4.1",
  },
  GPT_4_1_MINI: {
    key: "GPT_4_1_MINI",
    value: "gpt-4.1-mini",
    label: "GPT 4.1 Mini",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-4.1-mini",
  },
  GPT_5: {
    key: "GPT_5",
    value: "gpt-5",
    label: "GPT-5",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5",
  },
  GPT_5_4: {
    key: "GPT_5_4",
    value: "gpt-5.4",
    label: "GPT-5.4",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5.4",
  },
  GPT_5_5: {
    key: "GPT_5_5",
    value: "gpt-5.5",
    label: "GPT-5.5",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5.5",
  },
  GPT_5_6_SOL: {
    key: "GPT_5_6_SOL",
    value: "gpt-5.6-sol",
    label: "GPT-5.6 Sol",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5.6-sol",
  },
  GPT_6_ASTRA: {
    key: "GPT_6_ASTRA",
    value: "gpt-6-astra",
    label: "GPT-6 Astra",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-6-astra",
  },
  GPT_6_1_SOL: {
    key: "GPT_6_1_SOL",
    value: "gpt-6.1-sol",
    label: "GPT-6.1 Sol",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-6.1-sol",
  },
  GPT_6_SOL: {
    key: "GPT_6_SOL",
    value: "gpt-6-sol",
    label: "GPT-6 Sol",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-6-sol",
  },
  GPT_6_LUNA: {
    key: "GPT_6_LUNA",
    value: "gpt-6-luna",
    label: "GPT-6 Luna",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-6-luna",
  },
  GPT_5_6_TERRA: {
    key: "GPT_5_6_TERRA",
    value: "gpt-5.6-terra",
    label: "GPT-5.6 Terra",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5.6-terra",
  },
  GPT_5_6_LUNA: {
    key: "GPT_5_6_LUNA",
    value: "gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "gpt-5.6-luna",
  },
  MUSE: {
    key: "MUSE",
    value: "muse",
    label: "Muse",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    new: false,
    strides: false,
    scenesEngine: "muse",
  },
  MUSE1: {
    key: "MUSE1",
    value: "muse1",
    label: "Muse 1",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    new: false,
    strides: false,
    scenesEngine: "muse1",
  },
  MY_VOICE: {
    key: "MY_VOICE",
    value: "my-voice",
    label: "My Voice (Beta)",
    subLabel: "Writes like you",
    iconPath: "/assets/model-selection-modal/myvoice.svg",
    category: PROSE_MODE_CATEGORIES.STANDARD,
    scenesEngine: "myVoice",
    requiresMyVoice: true,
  },
  MYTHOMAX13B: {
    key: "MYTHOMAX13B",
    value: "openrouter",
    label: "Mythomax 13B",
    engine: "gryphe/mythomax-l2-13b",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  },
  // Removed by OpenRouter
  // Leaving this as comment for now in case it comes back (this has happened before)
  // MISTRAL_MEDIUM: {
  //   key: "MISTRAL_MEDIUM",
  //   value: "openrouter",
  //   label: "Mistral Medium",
  //   engine: "mistralai/mistral-medium",
  //   category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  // },
  DEEP_SEEK: {
    key: "DEEP_SEEK",
    value: "openrouter",
    label: "DeepSeek V3",
    engine: "deepseek/deepseek-chat",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
  },
  DEEP_SEEK_V31: {
    key: "DEEP_SEEK_V31",
    value: "deepSeekV31",
    label: "DeepSeek V3.1",
    scenesEngine: "deepSeekV31",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  DEEP_SEEK_V4_PRO: {
    key: "DEEP_SEEK_V4_PRO",
    value: "deepSeekV4Pro",
    label: "DeepSeek V4 Pro",
    scenesEngine: "deepSeekV4Pro",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
  },
  DEEP_SEEK_R1: {
    key: "DEEP_SEEK_R1",
    value: "deepseek",
    label: "DeepSeek R1",
    engine: "deepseek/deepseek-r1",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    scenesEngine: "deepseek-r1",
  },
  GROK_4_3: {
    key: "GROK_4_3",
    value: "openrouter",
    label: "Grok 4.3",
    engine: "x-ai/grok-4.3",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
    scenesEngine: "x-ai/grok-4.3",
  },
  GROK_4_7: {
    key: "GROK_4_7",
    value: "openrouter",
    label: "Grok 4.7",
    engine: "x-ai/grok-4.7",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
    scenesEngine: "x-ai/grok-4.7",
  },
  GLM_5_3: {
    key: "GLM_5_3",
    value: "openrouter",
    label: "GLM 5.3",
    engine: "z-ai/glm-5.3",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
    scenesEngine: "z-ai/glm-5.3",
  },
  GLM_5_3_FLASH: {
    key: "GLM_5_3_FLASH",
    value: "openrouter",
    label: "GLM 5.3 Flash",
    engine: "z-ai/glm-5.3-flash",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
    scenesEngine: "z-ai/glm-5.3-flash",
  },
  QWEN_3_8_MAX: {
    key: "QWEN_3_8_MAX",
    value: "openrouter",
    label: "Qwen 3.8 Max",
    engine: "qwen/qwen3.8-max",
    category: PROSE_MODE_CATEGORIES.EXPERIMENTAL,
    new: true,
    scenesEngine: "qwen/qwen3.8-max",
  },
};

// the center column width where we need to change styles
// for example, when we display icon with no title
export const SMALL_HEADER_BREAKPOINT = 600;
// when three buttons are displayed in the footer
// for example, the prose generate actions when paused
export const SMALL_THREE_BUTTON_BREAKPOINT = 600;
// the center column size at which we need to truncate button titles
// i.e. Add Character -> Add etc
export const COMPRESS_BUTTON_TITLES_BREAKPOINT = 795;

function StoryBible({
  subscriptionJSON,
  user,
  userData,
  projectId,
  documentId,
  setDocumentId,
  documentLength,
  project,
  editorSectionRef,
  centerColumnRef,
  onStyleGuidePointerClick,
  onStyleGuideStyleSave,
  styleGuideDisplayStyle = "",
  selectedStyleSource,
  selectedStyleName,
}) {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const sendFailureSignal = useStickyTourStore((state) => state.sendFailureSignal);
  const isActive = useStoryBibleStore((state) => state.isActive);
  const setIsActive = useStoryBibleStore((state) => state.setIsActive);
  const saved = useStoryBibleStore((state) => state.saved);
  const seed = useStoryBibleStore((state) => state.seed);
  const setSeed = useStoryBibleStore((state) => state.setSeed);
  const metadata = useStoryBibleStore((state) => state.metadata);
  const setMetadata = useStoryBibleStore((state) => state.setMetadata);
  const featuredStyles = useStoryBibleStore((state) => state.featuredStyles);
  const setFeaturedStyles = useStoryBibleStore((state) => state.setFeaturedStyles);
  const synopsis = useStoryBibleStore((state) => state.synopsis);
  const setSynopsis = useStoryBibleStore((state) => state.setSynopsis);
  const characters = useStoryBibleStore((state) => state.characters);
  const setCharacters = useStoryBibleStore((state) => state.setCharacters);
  const outline = useStoryBibleStore((state) => state.outline);
  const setOutline = useStoryBibleStore((state) => state.setOutline);
  const options = useStoryBibleStore((state) => state.options);
  const setOptions = useStoryBibleStore((state) => state.setOptions);
  const setSceneToWrite = useStoryBibleStore((state) => state.setSceneToWrite);
  const setOrphanedChapter = useStoryBibleStore((state) => state.setOrphanedChapter);
  const outlineObject = useStoryBibleStore((state) => state.outlineObject);
  const setOutlineObject = useStoryBibleStore((state) => state.setOutlineObject);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const recentInvocations = useStoryBibleStore((state) => state.recentInvocations);
  const setRecentInvocations = useStoryBibleStore((state) => state.setRecentInvocations);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) =>
    isStoryBibleCtaBlockingRetrigger(state.ctaAction),
  );
  const synopsisCtaShimmer = useCrossAppSignalStore((state) => state.signals[getSynopsisCTASignal()]);
  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const setIsStreaming = useStoryBibleStore((state) => state.setIsStreaming);
  const isStreamingRef = useStoryBibleStore((state) => state.isStreamingRef);
  const setIsProseStreaming = useStoryBibleStore((state) => state.setIsProseStreaming);
  const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
  const setOutOfQuota = useStoryBibleStore((state) => state.setOutOfQuota);
  const setStreamRequestRef = useStoryBibleStore((state) => state.setStreamRequestRef);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const setSceneId = useStoryBibleStore((state) => state.setSceneId);
  const requesting = useStoryBibleStore((state) => state.requesting);
  const setRequesting = useStoryBibleStore((state) => state.setRequesting);
  const setStreamTimedOut = useStoryBibleStore((state) => state.setStreamTimedOut);
  const abortStream = useStoryBibleStore((state) => state.abortStream);
  const abortController = useStoryBibleStore((state) => state.abortController);
  const history = useStoryBibleStore((state) => state.history);
  const setHistory = useStoryBibleStore((state) => state.setHistory);
  const elementRefs = useStoryBibleStore((state) => state.elementRefs);
  const createScene = useStoryBibleStore((state) => state.createScene);
  const requestingRef = useStoryBibleStore((state) => state.requestingRef);
  const helpContents = useStoryBibleStore((state) => state.helpContents);
  const setHelpContents = useStoryBibleStore((state) => state.setHelpContents);
  const helpPosition = useStoryBibleStore((state) => state.helpPosition);
  const isEmpty = useStoryBibleStore((state) => state.isEmpty);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const setStorePOVTense = useProjectStore((state) => state.setStorePOVTense);

  const setFeedbackOpen = useProjectStore((state) => state.setFeedbackOpen);
  const setFeedbackInvocation = useProjectStore((state) => state.setFeedbackInvocation);
  const structuredOutline = useStructuredOutlineStore((state) => state.outline);
  const outlinePromptRepresentation = useMemo(
    () => useStructuredOutlineStore.getState().getPromptRepresentation(),
    [structuredOutline],
  );
  const { setToast, dismissToast } = useToast();

  useCollapsedSections({ projectId });
  const collapsedSections = useStoryBibleStore((state) => state.collapsedSections);
  const toggleSectionCollapsed = useStoryBibleStore((state) => state.toggleSectionCollapsed);
  const setSectionCollapsed = useStoryBibleStore((state) => state.setSectionCollapsed);
  const toggleWorldbuildingCollapsed = useCallback(() => {
    toggleSectionCollapsed("worldbuilding");
  }, [toggleSectionCollapsed]);
  const setWorldbuildingCollapsed = useCallback((value) => {
    setSectionCollapsed("worldbuilding", value);
  }, [setSectionCollapsed]);
  const toggleCharactersCollapsed = useCallback(() => {
    toggleSectionCollapsed("characters");
  }, [toggleSectionCollapsed]);
  const setCharactersCollapsed = useCallback((value) => {
    setSectionCollapsed("characters", value);
  }, [setSectionCollapsed]);
  const toggleSeedCollapsed = useCallback(() => {
    toggleSectionCollapsed("seed");
  }, [toggleSectionCollapsed]);
  const toggleGenreCollapsed = useCallback(() => {
    toggleSectionCollapsed("genre");
  }, [toggleSectionCollapsed]);
  const toggleStyleCollapsed = useCallback(() => {
    toggleSectionCollapsed("style");
  }, [toggleSectionCollapsed]);
  const toggleSynopsisCollapsed = useCallback(() => {
    toggleSectionCollapsed("synopsis");
  }, [toggleSectionCollapsed]);
  const toggleOutlineCollapsed = useCallback(() => {
    toggleSectionCollapsed("outline");
  }, [toggleSectionCollapsed]);

  // Tour context for credit tracking
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const tourSteps = useStickyTourStore((state) => state.steps);
  const activeTourVariation = useStickyTourStore((state) => state.activeTourVariation);

  // For pulling up data pertaining to other projects in a series
  const getScenesByProjectId = useSeriesStore((state) => state.getScenesByProjectId);

  const [synopsisRewrite, setSynopsisRewrite] = useState("");
  const [outlinePickerOpen, setOutlinePickerOpen] = useState(false);
  const [outlineType, setOutlineType] = useState(OUTLINE_TYPES[0]);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    setSyncing(false);
  }, [projectId]);

  const clientId = useRef(null);
  const [engine, setEngine] = useState(null);
  const [stride, setStride] = useState(null);

  const [invocations, updateInvocations] = useImmer([]);
  const invocationsRef = useRef(invocations);

  const [adminOpen, setAdminOpen] = useState(false);

  const { can } = useAdminAccess();
  const admin = can("admin.tools");

  const data = { seed, metadata, synopsis, characters, outline: outlinePromptRepresentation, scenes };

  const orderedScenes = scenes ? orderedScenesBySectionAndScene(scenes) : [];
  const sceneCount = orderedScenes.length;
  const [saveError, setSaveError] = useState(false);
  const persistentSaveErrorToastRef = useRef(false);
  const [focusBraindump, setFocusBraindump] = useState(false);
  const requestTimeoutsRef = useRef([]);

  const navHistory = useHistory();
  const empty = isEmpty();

  const customModels = useSudowriteStore((state) => state.customModels);
  const subscription = useSudowriteStore((state) => state.subscription);
  const supportFormUrl = useMemo(() => getAirTableSupportUrl(subscription), [subscription]);
  const proseModes = useStoryBibleStore((state) => state.PROSE_MODES);
  const setProseModes = useStoryBibleStore((state) => state.setProseModes);

  const savedRef = useRef(saved);

  const getDraftDefaults = useSudowriteStore((state) => state.getDraftDefaults);
  const currentProjectId = project?.id;

  const usesScenes = project.useScenes;
  const hasAccessToStyleGuide = !!subscriptionJSON?.hasAccessToStyleGuide;
  const isReadOnlySudowriteStyle = hasAccessToStyleGuide && selectedStyleSource === "sudowrite";
  const styleInputLimit = getStyleInputLimit(hasAccessToStyleGuide);
  const [styleGuideDraft, setStyleGuideDraft] = useState(styleGuideDisplayStyle ?? "");
  const lastSyncedStyleGuideDisplayRef = useRef(styleGuideDisplayStyle ?? "");

  // Preserve dirty editable drafts during async list updates, but a read-only
  // Sudowrite selection must always show its own instructions.
  useEffect(() => {
    const nextDisplay = styleGuideDisplayStyle ?? "";
    setStyleGuideDraft((currentDraft) => {
      const lastSyncedDisplay = lastSyncedStyleGuideDisplayRef.current;
      lastSyncedStyleGuideDisplayRef.current = nextDisplay;
      return resolveStyleGuideDraft({
        currentDraft,
        lastSyncedDisplay,
        nextDisplay,
        isReadOnlySudowriteStyle,
      });
    });
  }, [isReadOnlySudowriteStyle, styleGuideDisplayStyle]);
  // Turn on SB by default first time project is opened if there is existing SE info in it
  useEffect(() => {
    if ((isActive === null || typeof isActive === "undefined") && !empty) {
      setIsActive(true);
    }
  }, [isActive, empty]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("sb")) {
      setFocusBraindump(true);
    }
  }, []);

  useEffect(() => {
    // Rebuild custom entries when retirement metadata changes, even if the
    // number of records is unchanged. Never retain a stale retired picker entry.
    const proseModes = Object.fromEntries(Object.entries(useStoryBibleStore.getState().PROSE_MODES)
      .filter(([key]) => !key.startsWith("CUSTOM_")));
    if (Object.keys(proseModes).length > 0) {
      let customModelsAvailable = customModels.filter(isSelectableLegacyVoiceModel);

      customModelsAvailable = customModelsAvailable
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((i, ind) => {
          const idShort = i.id.split("-")[0];
          const checkpointSteps = i.id.split("_")[1];
          const checkpointStepString = checkpointSteps ? `_${checkpointSteps}` : "";
          return {
            label: `My Voice ${customModelsAvailable.length - ind}`,
            key: `CUSTOM_${i.id}`,
            engine: `CUSTOM_${i.id}${checkpointStepString}`,
            value: "custom",
            subLabel: `${i.originalFilename.split(".")[0]}\n${new Date(i.createdAt).toLocaleString()}
ID: ${idShort + checkpointStepString}-${i.lr}-${i.ne}-${i.modelSize === "Small" ? "s" : "b"}
Version: ${i.version ? i.version : "1"}`,
            type: "Custom",
            category: PROSE_MODE_CATEGORIES.CUSTOM,
            version: i.version ? i.version : "1",
          };
        });
      const customModelsObject = customModelsAvailable.reduce((obj, item) => {
        obj[item.key] = item;
        return obj;
      }, {});
      const combined = { ...proseModes, ...customModelsObject };
      setProseModes(combined);
    }
  }, [customModels, setProseModes]);

  // Abort the stream if user navigates from current page
  useEffect(() => {
    const unlisten = navHistory.listen((location, action) => {
      abortStream();
    });

    return () => unlisten();
  }, []);

  useEffect(() => {
    if (!documentId) return;
  }, [documentId]);

  useEffect(() => {
    invocationsRef.current = invocations;
  }, [invocations]);

  useEffect(() => {
    if (!requesting) {
      setOutlineObject(new LegacyOutline(outline));
    }
  }, [outline, requesting]);

  useEffect(() => {
    if (!outlineObject) return;
    setOptions(outlineObject.groupedOptions()); //set the options for the select which you use to set up columns
  }, [outlineObject]);

  // Populates the options for outline selections
  // Order matters here - this shoudl come before the handler for adding/deleting outlines
  useEffect(() => {
    const scene = scenes?.[sceneId];
    if (!scene) return;

    if (project.useStructuredOutline) {
      const scenesToSearch = project.seriesId ? getScenesByProjectId(projectId) : structuredOutline?.content?.items;
      const located = scenesToSearch.find((item) => item.id === scene.itemId);

      // If the scene has an itemId, but it is not located in the outline, the document was previously linked to an outline section that was removed
      setOrphanedChapter(scene.itemId && !located);

      if (located) {
        const summary = extractTextFromTipTap(located.summary);
        setSceneToWrite({ label: located.name, summary, value: located.id });
      } else {
        setSceneToWrite(null);
      }
      return;
    }

    if (options[scene.section] && options[scene.section].options[scene.scene]) {
      setSceneToWrite(options[scene.section].options[scene.scene]);
    } else {
      setSceneToWrite(null);
    }
  }, [options, sceneId, scenes, project, structuredOutline, setSceneToWrite, getScenesByProjectId, projectId]);

  useEffect(() => {
    if (!options || options.length === 0 || !project) {
      return;
    }
    if (project.data.isStoryBible) {
      return;
    }
    if (clientConversionSingletons[project.id]) {
      return;
    }
    clientConversionSingletons[project.id] = true;

    setProseGeneratorActive(true);

    const convertToStoryBible = async () => {
      await Auth.fetchFileWithIdToken(`/api/projects/${project.id}/convert-to-story-bible`, { options });
    };
    convertToStoryBible();
  }, [options.length, project]);

  const prevHadCreditsRef = useRef(subscriptionJSON.totalQuota > 0);

  useEffect(() => {
    const nowOutOfQuota = subscriptionJSON.totalQuota <= 0;
    const wasInQuota = prevHadCreditsRef.current;

    if (nowOutOfQuota) {
      setOutOfQuota(true);

      // Fire event only on transition to out of credits
      if (wasInQuota) {
        const isBonus = subscriptionJSON.grace === true;
        const tourContext = activeTourId
          ? { tourId: activeTourId, stepId: tourSteps[currentStepIndex]?.id, stepIndex: currentStepIndex, variation: activeTourVariation }
          : undefined;
        AppEventPublisher.credits.outOfCredits(isBonus, tourContext);
      }
    } else {
      setOutOfQuota(false);
    }

    prevHadCreditsRef.current = !nowOutOfQuota;
  }, [subscriptionJSON, activeTourId, currentStepIndex, tourSteps, activeTourVariation]);

  // Persist story bible data
  const { loading, loadingState, forceSave } = usePersist({
    projectId,
    isActive,
    seed,
    characters,
    synopsis,
    outline,
    metadata,
    scenes,
    setSaveError,
    setFeaturedStyles,
    stripLegacyStyleFromSave: hasAccessToStyleGuide,
  });

  // Store forceSave in the store so Find/Replace can access it
  useEffect(() => {
    useStoryBibleStore.getState().setForceSave(forceSave);
  }, [forceSave]);

  useEffect(() => {
    savedRef.current = saved;
  }, [saved]);

  const markStoryBibleUnsaved = useCallback(() => {
    useStoryBibleStore.getState().setSaved(false);
    savedRef.current = false;
  }, []);

  // Load story bible fields (seed, genre, style, synopsis) on focus.
  // When triggered by a field focus (external=true), we snapshot the current store
  // values before fetching. After the fetch, we only update fields whose server
  // value differs from the snapshot — meaning another session changed them. This
  // lets the user keep typing without the field going read-only during the sync.
  const loadStoryBibleFields = useCallback(
    async (external) => {
      const origin = useStoryBibleStore.getState();
      if (!isCurrentStoryBibleOwner(origin, projectId)) return;
      try {
        // When switching between fields, a save may still be in-flight (the
        // blur handler calls forceSave, but the PUT hasn't completed yet).
        // Fetching now would return stale server data and overwrite the
        // unsaved local edits — so skip the refetch until the save lands.
        if (external && !savedRef.current) {
          return;
        }

        // Snapshot current values directly from the store
        const snap = external ? useStoryBibleStore.getState() : null;

        if (external) setSyncing(true);

        const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
        const storyEngine = results?.storyEngine;
        if (!storyEngine || !isCurrentStoryBibleOwner(origin, projectId)) return;

        if (snap) {
          // Only update fields that actually changed on the server
          if (storyEngine.seed !== snap.seed) {
            setSeed(storyEngine.seed);
          }
          if (storyEngine.synopsis !== snap.synopsis) {
            setSynopsis(storyEngine.synopsis, projectId);
          }
          if (JSON.stringify(storyEngine.metadata) !== JSON.stringify(snap.metadata)) {
            setMetadata(() => storyEngine.metadata);
          }
        } else if (savedRef.current) {
          // Non-external loads (e.g. initial mount) — update everything if saved
          setSeed(storyEngine.seed);
          setSynopsis(storyEngine.synopsis, projectId);
          setMetadata(() => storyEngine.metadata);
        }
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "story_bible_fields_refetch" } });
        if (isCurrentStoryBibleOwner(origin, projectId)) setToast("Failed to refresh story bible data");
      } finally {
        if (external && isCurrentStoryBibleOwner(origin, projectId)) setSyncing(false);
      }
    },
    [projectId, setSeed, setSynopsis, setMetadata, setToast]
  );

  useRefetchOnFocus(loadStoryBibleFields, [projectId, loadStoryBibleFields], { skipInitialFetch: true });

  useEffect(() => {
    const saveErrorToast = resolveSaveErrorToast(saveError);
    if (saveErrorToast) {
      if (saveErrorToast.persistent) {
        setToast(saveErrorToast.message, { durationMs: Infinity, dismissible: true });
        // Leave saveError set so a later successful save dismisses the toast
        persistentSaveErrorToastRef.current = true;
      } else {
        setToast(saveErrorToast.message);
        setSaveError(false);
      }
    } else if (persistentSaveErrorToastRef.current) {
      persistentSaveErrorToastRef.current = false;
      dismissToast();
    }
  }, [saveError]);

  // Toggle the admin menu when ⌘L is pressed
  useEffect(() => {
    if (admin) {
      const down = (e) => {
        if (e.key === "l" && e.metaKey) {
          setAdminOpen((open) => !open);
        }
      };

      document.addEventListener("keydown", down);
      return () => document.removeEventListener("keydown", down);
    }
  }, []);

  useEffect(() => {
    const cookies = new Cookies();

    // Set a unique client id
    if (cookies.get("client_id")) {
      clientId.current = cookies.get("client_id");
    } else {
      clientId.current = makeId(25);

      cookies.set("client_id", clientId.current, {
        domain: process.env.NODE_ENV === "production" ? ".sudowrite.com" : "localhost",
        maxAge: 60 * 60 * 24 * 365 * 4,
        path: "/",
      });
    }
  }, []);

  useEffect(() => {
    if (!documentId || loadingState) return;
    if (typeof scenes === "undefined") return;

    const existingScene = findSceneByDocumentId(scenes, documentId);
    if (existingScene) {
      setSceneId(existingScene.id);
      return;
    }

    const createdId = createScene(documentId);
    try {
      const { modelKey, creativity } = getDraftDefaults(currentProjectId);
      if (modelKey || typeof creativity === "number") {
        setScenes((draft) => {
          if (!draft[createdId]) return;
          if (modelKey && proseModes?.[modelKey]) {
            draft[createdId].proseMode = modelKey;
          }
          if (typeof creativity === "number") {
            draft[createdId].creativityLevel = creativity;
          }
        });

        if (modelKey && !metadata?.draftProseMode) {
          setMetadata((draft) => {
            draft.draftProseMode = modelKey;
          });
        }
      }
    } catch (err) {
      try {
        Sentry.captureException(err, { tags: { projectId: currentProjectId || "unknown", feature: "draft_defaults_init" } });
      } catch (_) {}
    }

    setSceneId(createdId);
  }, [scenes, documentId, loadingState, setSceneId, createScene, proseModes, getDraftDefaults, currentProjectId, setScenes, metadata?.draftProseMode, setMetadata]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isStreaming || requesting) {
        event.preventDefault();
        event.returnValue = ""; // This has to be blank for modern Chrome; the message is no longer customizable
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isStreaming, requesting]);

  const handleH1Click = () => {
    elementRefs.top?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateProjectTitle = debounce((text) => {
    project.automaticallyTitleProject(text);
  }, 2000);

  const handleTextareaInput = async (e, key) => {
    const length = countWords(e.target.value);
    const limit = key === FUNCTION_NAMES.STYLE ? styleInputLimit : INPUT_LIMITS[key];

    const toastMessage = resolveInputToast(e.target.value, metadata?.[key], limit);
    if (toastMessage) {
      setToast(toastMessage);
    }

    if (project.isUntitled() && AUTOMATIC_TITLE_SECTIONS.includes(key) && length > MIN_DOC_TITLING_WORDS) {
      generateProjectTitle(e.target.value);
    }
  };

  function findSceneByDocumentId(data, id) {
    for (let key in data) {
      if (data[key].contents && data[key].contents.id === id) {
        return data[key];
      }
    }
    return null;
  }

  /**
   * Stream a request from the server
   * @param {string} url URL to request
   * @param {object} additionalData Additional data that is combined with SE data
   * @param {function} update Function to update when receiving text chunk
   * @param {AbortSignal} abortSignal Abort signal to cancel the request
   * @param {string} requestType Type of request
   * @param {function} onChunk Function to call on each chunk for custom functionality that isn't just text inserts
   * @param {function} onEnd Function to call on when the stream is complete
   * @param {function} onTimeout Function to call on when the stream times out
   * @param {function} onStart Function to call on when starting to stream
   * @param {function} onError Function to call on when an error occurs
   * @returns
   *
   */
  const streamRequest = useCallback(
    async (
      url,
      additionalData, //
      update,
      abortSignal = null,
      requestType = null,
      onChunk = null,
      onEnd = null,
      onTimeout = null,
      onStart = null,
      onError = null
    ) => {
      const buildErrorToastContent = (err) => {
        if (isProbableModelOutageError(err)) {
          return {
            content: (
              <ModelOutageMessage
                supportUrl={supportFormUrl}
                className="text-app-danger-color"
                linkClassName="hover:brightness-[1.50] transition-all duration-200 ease-in-out underline text-app-danger-color"
              />
            ),
            options: {
              dismissible: true,
              durationMs: 15000,
            },
          };
        }

        return {
          content: normalizeErrorMessage(err),
          options: {},
        };
      };

      const showErrorToast = (err) => {
        const { content, options } = buildErrorToastContent(err);
        setToast(content, options);
      };

      // If SB is not active, don't include base localData fields
      let localData = isActive ? { seed, metadata, synopsis, characters, outline: outlinePromptRepresentation, scenes, projectId } : {};
      if (additionalData) {
        localData = { ...localData, ...additionalData };
      }

      setRequesting(true);
      setStreamTimedOut(false);
      localData.prompt = localData.input; // add prompt key for downstream readability
      localData.invocationType = "storyEngine";
      localData.stride = stride?.value;
      localData.documentId = documentId;

      const clearTimeouts = () => {
        requestTimeoutsRef.current.forEach((t) => clearTimeout(t));
        requestTimeoutsRef.current = [];
      };

      let buffer = "";
      let animationFrameId = null;

      // debounce the update function to prevent too many renders
      // Use requestAnimationFrame for smoother updates and longer debounce to prevent browser lockup
      const debouncedUpdate = debounce(
        () => {
          // Cancel any pending animation frame
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }

          // Use requestAnimationFrame for smooth, non-blocking updates
          animationFrameId = requestAnimationFrame(() => {
            update(buffer);
            buffer = "";
            animationFrameId = null;
          });
        },
        sceneCount > 10 ? 150 : 100,
        {
          maxWait: sceneCount > 10 ? 300 : 200,
        }
      );

      try {
        const result = await Auth.streamWithIdToken(
          url,
          localData,
          abortSignal,
          (chunk) => {
            clearTimeouts();

            const timeoutLength = localData?.engine?.includes("deepseek") ? 180_000 : 45_000;

            const timeoutId = setTimeout(() => {
              if (requestType?.startsWith("sceneProse")) return;
              console.log("Timeout triggered");
              if (!requestingRef.current) return; // Early return if aborted manually
              console.log("Timeout past early return");
              setStreamTimedOut(true);
              abortStream();
              setRequesting(false);
              setIsStreaming(false);
              onTimeout && onTimeout();
              setToast("Looks like the request timed out. Please try again.");
              sendInvocationEvent(invocationsRef.current?.[0]?.id || "unknown", "stream-timeout");
              Sentry.captureMessage("Request timed out during Story Bible completion", {
                tags: {
                  user: userData?.userId,
                  userEmail: userData?.data?.email,
                  url: url,
                },
              });
            }, timeoutLength);

            requestTimeoutsRef.current.push(timeoutId);

            if (update && chunk.text) {
              if (!isStreamingRef.current) {
                setIsStreaming(true);

                // Handle the special case of prose streaming
                // Allows the main editor to toggle disabled while prose is streaming
                if (requestType?.startsWith("sceneProse")) {
                  setIsProseStreaming(true);
                }
              }

              // concatenate into a buffer and let debounce let it through
              buffer += chunk.text;
              debouncedUpdate();
            }

            if (chunk.initialInvocationId) {
              updateInvocations((draft) => {
                draft.unshift({ url, id: chunk.initialInvocationId, createdAt: new Date() });
              });

              if (requestType) {
                setRecentInvocations((prevState) => {
                  const newState = { ...prevState };
                  if (requestType === "synopsis") {
                    newState.synopsis = chunk.initialInvocationId;
                  } else if (requestType === "characters") {
                    newState.characters = chunk.initialInvocationId;
                  } else if (requestType === "outline") {
                    newState.outline = chunk.initialInvocationId;
                  } else if (requestType.startsWith("commands")) {
                    const sceneId = requestType.split("-")[1];
                    newState.commands[sceneId] = chunk.initialInvocationId;
                  } else if (requestType.startsWith("sceneProse")) {
                    const sceneId = requestType.split("-")[1];
                    newState.sceneProse[sceneId] = chunk.initialInvocationId;
                  }
                  newState.lastInvocationType = requestType.split("-")[0];
                  newState.lastInvocationId = chunk.initialInvocationId;
                  return newState;
                });
              }
            }

            if (onChunk) {
              onChunk(chunk);
            }

            if (chunk.error) {
              // Flush remaining buffer and cleanup
              debouncedUpdate.cancel();
              if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
              }
              if (buffer) {
                update(buffer);
                buffer = "";
              }

              setRequesting(false);
              setIsStreaming(false);
              clearTimeouts();

              showErrorToast(chunk.error);
            }
          },
          // Flush remaining buffer and cleanup on successful completion
          () => {
            debouncedUpdate.cancel();
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            if (buffer) {
              update(buffer);
              buffer = "";
            }

            setRequesting(false);
            setIsStreaming(false);
            clearTimeouts();
            if (onEnd) {
              onEnd();
            }
          },
          (error) => {
            const isAbortMessage = error?.message?.includes("abort");
            const isSceneProse = requestType?.startsWith("sceneProse");

            // Flush remaining buffer and cleanup on error
            debouncedUpdate.cancel();
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            if (buffer) {
              update(buffer);
              buffer = "";
            }

            setRequesting(false);
            setIsStreaming(false);
            clearTimeouts();
            const handledByCaller = onError?.(error) === true;
            if (!(isSceneProse && isAbortMessage) && !handledByCaller) {
              showErrorToast(error);
            }
          },
          null,
          onStart
        );

        // Final cleanup
        debouncedUpdate.cancel();
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        clearTimeouts();

        setRequesting(false);
        return result;
      } catch (e) {
        clearTimeouts();
        setRequesting(false);
        setIsStreaming(false);
        const handledByCaller = onError?.(e) === true;
        const isAbortMessage = e?.message?.includes("abort");
        const isSceneProse = requestType?.startsWith("sceneProse");
        if (!(isSceneProse && isAbortMessage) && !handledByCaller) {
          showErrorToast(e);
        }
      }
    },
    [data, supportFormUrl]
  );

  setStreamRequestRef(streamRequest);

  const emptySeed = !seed || seed?.trim().length === 0;

  // Callbacks
  // TODO: this is very very not DRY. need to refactor

  const seedOnInput = useCallback((input) => handleTextareaInput(input, FUNCTION_NAMES.SEED), [metadata]);
  const genreOnInput = useCallback((input) => handleTextareaInput(input, FUNCTION_NAMES.GENRE), [metadata]);
  const styleOnInput = useCallback((input) => handleTextareaInput(input, FUNCTION_NAMES.STYLE), [styleInputLimit, metadata]);
  const synopsisOnInput = useCallback((input) => handleTextareaInput(input, FUNCTION_NAMES.SYNOPSIS), [metadata]);

  const rewriteSynopsis = async () => {
    const origin = useStoryBibleStore.getState();
    let out = "";
    await streamRequest(
      "/api/ai/storyEngine/rewrite/synopsis",
      { feedback: synopsisRewrite, rewriteType: "synopsis" },
      (text) => {
        out += text;
        if (isCurrentStoryBibleOwner(origin, projectId)) setSynopsis(out.trim(), projectId, false, setStorePOVTense);
      }, // update
      abortController.current.signal, // abortSignal
      undefined, // requestType
      undefined, // onChunk
      () => {
        sendFlowSignal(TourSignal.STORY_BIBLE_SYNOPSIS_REWRITE_COMPLETED);
      }, // onEnd
      undefined, // onTimeout
      undefined, // onStart
      undefined // onError
    );
  };

  const setGenre = useCallback((v) => {
    markStoryBibleUnsaved();
    setMetadata((draft) => {
      draft.genre = v;
    });
  }, [markStoryBibleUnsaved, setMetadata]);

  const handleStoryBibleFieldBlur = useCallback(() => {
    forceSave();
  }, [forceSave]);

  const setStyle = useCallback(
    async (value, options) => {
      if (isReadOnlySudowriteStyle) return;
      markStoryBibleUnsaved();
      if (hasAccessToStyleGuide) {
        setStyleGuideDraft(value);
        if (options?.writingSample) {
          await onStyleGuideStyleSave?.({
            instructions: value,
            writingSample: options.writingSample,
            source: "generated",
          });
        }
        return;
      }

      setMetadata((draft) => {
        draft.style = value;
      });
    },
    [hasAccessToStyleGuide, isReadOnlySudowriteStyle, markStoryBibleUnsaved, onStyleGuideStyleSave, setMetadata],
  );

  const styleMetadata = hasAccessToStyleGuide ? { ...metadata, style: styleGuideDraft } : metadata;

  const handleStyleBlur = useCallback(() => {
    if (isReadOnlySudowriteStyle) return;
    handleStoryBibleFieldBlur();
    if (!hasAccessToStyleGuide) return;
    void onStyleGuideStyleSave?.({
      instructions: styleGuideDraft,
      source: "story_bible",
    });
  }, [handleStoryBibleFieldBlur, hasAccessToStyleGuide, isReadOnlySudowriteStyle, onStyleGuideStyleSave, styleGuideDraft]);

  const openSeedHistory = useStoryBibleHistory("seed", (contents) => setSeed(contents));
  const openGenreHistory = useStoryBibleHistory("genre", setGenre);
  const openStyleHistory = useStoryBibleHistory("style", setStyle);
  const openSynopsisHistory = useStoryBibleHistory("synopsis", (contents) => setSynopsis(contents, projectId));

  // For the three generate functions below, we are no longer setting a toast, but
  // disabling the button and showing a Hover Tip (with HoverTippable)
  // I am leaving the guard clause just to be safe, but these should not be
  // invoked without the correct values already in the Story Bible

  const generateSynopsis = useCallback(
    (e) => {
      if (emptySeed) {
        return;
      }

      setSynopsis("", projectId, false, setStorePOVTense);

      const origin = useStoryBibleStore.getState();
      let out = "";

      streamRequest(
        "/api/ai/storyEngine/synopsis",
        null,
        (text) => {
          if (!out) {
            out += text.trimStart();
          } else {
            out += text;
          }
          if (isCurrentStoryBibleOwner(origin, projectId)) setSynopsis(out, projectId, false, setStorePOVTense);
        },
        abortController.current.signal,
        "synopsis",
        null, // onChunk
        () => {
          sendFlowSignal(TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_COMPLETED);
        }, // onEnd
        null, // onTimeout
        null, // onStart
        () => {
          sendFailureSignal(TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_FAILED);
        } // onError
      );

      return true;
    },
    [emptySeed, data, projectId, sendFlowSignal, sendFailureSignal, setToast]
  );

  const generateSynopsisDisabledMessage = (
    <>
      Please write a <strong>Braindump</strong> to generate a synopsis
    </>
  );

  if (!isActive)
    return (
      <StyledContainer className="story-bible-component-container" data-story-bible-content>
        <StoryBibleSectionInViewTracker centerColumnRef={centerColumnRef} />
        {helpContents && (
          <HelpModal
            helpPosition={helpPosition}
            helpContents={helpContents}
            close={() => setHelpContents(null)}
          />
        )}

        {history && (
          <History
            storyEngineId={history.storyEngineId} //
            snapKey={history.key}
            id={history.id}
            user={user}
            title={history.title}
            close={() => setHistory(null)}
            onRestore={history.onRestore}
          />
        )}
      </StyledContainer>
    );

  const displayLegacyOutline = outline !== null && !project.useStructuredOutline;
  const showChapterCreator = structuredOutline.content?.items?.length > 0;

  return (
    <StyledContainer className="story-bible-component-container" data-story-bible-content>
      <StoryBibleSectionInViewTracker centerColumnRef={centerColumnRef} />
      <div className="section chapter-creator chapter-creator-top">
        {displayLegacyOutline && (
          <ChapterCreatorBox
            placement="top"
            mode="light"
            documentLength={documentLength}
            project={project}
            documentId={documentId}
            editorSectionRef={editorSectionRef}
            createScene={createScene}
            setDocumentId={setDocumentId}
            saved={saved}
          />
        )}
        {showChapterCreator && (
          <CreateDocument
            key={`cd-${documentId}`}
            project={project}
            documentId={documentId}
            setDocumentId={setDocumentId}
            mode={CREATE_DOCUMENT_DISPLAY_MODES.followsDocument}
          />
        )}
      </div>
      <div className="inner">
        <div
          className="anchor"
          id="story-bible-top"
          tabIndex={-1}
          ref={(ref) => (elementRefs.top.current = ref)}
        ></div>
        {seed !== null && metadata !== null && (
          <div className="container">
            <div className="section story-bible-2-title">
              <h1>Story Bible</h1>
              <div className="story-bible-help-icon-container">
                <IconButton
                  src="/assets/help-icon.svg"
                  size={32}
                  iconSize={16}
                  variant="secondary"
                  onClick={() => {
                    window.open("https://docs.sudowrite.com/using-sudowrite/1ow1qkGqof9rtcyGnrWUBS/what-is-story-bible/jmWepHcQdJetNrE991fjJC", "_blank");
                  }}
                  hoverTipText="Learn more about Story Bible"
                />
              </div>
              <p>
                Track the key details of your story's world to improve Sudowrite's suggestions or fill it up step-by-step to grow your idea into a first draft.{" "}
                {project.seriesId && "Characters, Worldbuilding, and Outline are shared across all books in a series."}
              </p>
            </div>
            <div
              className="section seed"
              id="story-bible-seed"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-seed"
              ref={(ref) => (elementRefs.seed.current = ref)}
            >
              <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_SEED} />
              <CopyableTextarea
                innerRef={(ref) => (elementRefs.seed.current = ref)}
                type={FUNCTION_NAMES.SEED}
                fieldKey="braindump"
                headingId="heading-seed"
                title="Braindump"
                help={`This section affects: Synopsis`}
                history={openSeedHistory}
                placeholder={`Write a braindump of everything you know about the story. You can include information about plot, characters, worldbuilding, theme - anything!`}
                value={seed}
                setValue={(value) => {
                  setSeed(value);
                  sendFlowSignal(TourSignal.STORY_BIBLE_SEED_INPUT);
                }}
                onInput={seedOnInput}
                onBlur={handleStoryBibleFieldBlur}
                maxLength={INPUT_LIMITS[FUNCTION_NAMES.SEED]}
                iconPath={FUNCTION_ICONS[FUNCTION_NAMES.SEED]}
                height="short"
                ariaLabel="Braindump"
                containerClassName={"story-bible"}
                startFocused={focusBraindump}
                syncing={syncing}
                onFocusCallback={() => loadStoryBibleFields(true)}
                collapsed={collapsedSections.seed}
                onToggleCollapse={toggleSeedCollapsed}
              />

            </div>
            <div
              className="section genre"
              id="story-bible-genre"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-genre"
              ref={(ref) => (elementRefs.genre.current = ref)}
            >
              <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_GENRE} />
              <CopyableTextarea
                innerRef={(ref) => (elementRefs.genre.current = ref)}
                headingId="heading-genre"
                title="Genre"
                fieldKey="genre"
                help={`This section affects: Synopsis, Outline, ${usesScenes ? "Scenes" : "Beats"}, and Draft`}
                type={FUNCTION_NAMES.GENRE}
                history={openGenreHistory}
                placeholder={`What genre are you writing in? Feel free to include sub-genres and tropes.

Examples: Romance, Horror, Fantasy, Cozy mystery, Friends-to-Lovers, Gumshoe`}
                value={metadata?.genre || ""}
                setValue={(v) => {
                  setGenre(v);
                  sendFlowSignal(TourSignal.STORY_BIBLE_GENRE_INPUT);
                }}
                onInput={genreOnInput}
                onBlur={handleStoryBibleFieldBlur}
                maxLength={INPUT_LIMITS[FUNCTION_NAMES.GENRE]}
                iconPath={FUNCTION_ICONS[FUNCTION_NAMES.GENRE]}
                height="short"
                ariaLabel="Genre"
                containerClassName={"story-bible"}
                syncing={syncing}
                onFocusCallback={() => loadStoryBibleFields(true)}
                collapsed={collapsedSections.genre}
                onToggleCollapse={toggleGenreCollapsed}
              />

            </div>
            <div
              className="section style"
              id="story-bible-style"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-style"
              ref={(ref) => (elementRefs.style.current = ref)}
            >
              <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_STYLE} />
              <Style
                innerRef={(ref) => (elementRefs.style.current = ref)}
                help={`This section affects: ${usesScenes ? "Scenes" : "Beats"} and Draft`}
                onOpenHistory={openStyleHistory}
                metadata={styleMetadata}
                setStyle={async (value, options) => {
                  await setStyle(value, options);
                  sendFlowSignal(TourSignal.STORY_BIBLE_STYLE_CLICKED);
                }}
                onInput={styleOnInput}
                onBlur={handleStyleBlur}
                featuredStyles={featuredStyles}
                syncing={syncing}
                onFocusCallback={() => loadStoryBibleFields(true)}
                collapsed={collapsedSections.style}
                onToggleCollapse={toggleStyleCollapsed}
                headingId="heading-style"
                showStyleGuidePointer={hasAccessToStyleGuide}
                onStyleGuidePointerClick={onStyleGuidePointerClick}
                selectedStyleSource={selectedStyleSource}
                selectedStyleName={selectedStyleName}
                styleInputLimit={styleInputLimit}
              />

            </div>

            <div
              className="section synopsis"
              id="story-bible-synopsis"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-synopsis"
              ref={(ref) => (elementRefs.synopsis.current = ref)}
            >
              <CopyableTextarea
                generate={hasAccessToAgenticChat ? () => {
                  if (globalCtaActive) return;
                  emitLayoutEvent("trigger_cta_chat", {
                    type: "generate_synopsis",
                    projectId,
                    displayTitle: "Generate",
                    userMessage: "Generate Synopsis",
                    cannedResponse: "Ok, let's do it!",
                  });
                  return true;
                } : generateSynopsis}
                generateLabel={"Generate Synopsis"}
                generateDisabled={(hasAccessToAgenticChat && globalCtaActive) || (emptySeed && !synopsis)}
                generateDisabledMessage={generateSynopsisDisabledMessage}
                history={openSynopsisHistory}
                placeholder={`Introduce the characters, their goals, and the central conflict, while conveying the story's tone, themes, and unique elements.`}
                title="Synopsis"
                headingId="heading-synopsis"
                fieldKey="synopsis"
                help={`This section affects: Characters, Worldbuilding, Outline, and ${usesScenes ? "Scenes" : "Beats"}`}
                type={FUNCTION_NAMES.SYNOPSIS}
                recentInvocations={recentInvocations}
                setRecentInvocations={setRecentInvocations}
                requesting={requesting}
                value={synopsis}
                setValue={(value) => {
                  setSynopsis(value, projectId, false, setStorePOVTense);
                  sendFlowSignal(TourSignal.STORY_BIBLE_SYNOPSIS_EDITED);
                }}
                onInput={synopsisOnInput}
                onBlur={handleStoryBibleFieldBlur}
                maxLength={INPUT_LIMITS[FUNCTION_NAMES.SYNOPSIS]}
                iconPath={FUNCTION_ICONS[FUNCTION_NAMES.SYNOPSIS]}
                abortStream={abortStream}
                setFeedbackOpen={setFeedbackOpen}
                setFeedbackInvocation={setFeedbackInvocation}
                outOfQuota={outOfQuota}
                loadAnimationDuration={10}
                isStreaming={isStreaming}
                feedbackValue={synopsisRewrite}
                setFeedbackValue={setSynopsisRewrite}
                onRewriteWithFeedback={hasAccessToAgenticChat ? () => {
                  if (globalCtaActive) return;
                  emitLayoutEvent("trigger_cta_chat", {
                    type: "rewrite_synopsis",
                    projectId,
                    displayTitle: "Rewrite",
                    userMessage: "Rewrite Synopsis",
                    cannedResponse: "Ok, let's do it!",
                  });
                } : rewriteSynopsis}
                rewritePlaceholder={`Write instructions for how Sudowrite should rewrite your Synopsis. e.g. Make the twist more dramatic. Make the low point even lower. Lean into the tropes of this genre more.`}
                height="short"
                ariaLabel="Synopsis"
                containerClassName={"story-bible"}
                syncing={syncing}
                onFocusCallback={() => loadStoryBibleFields(true)}
                collapsed={collapsedSections.synopsis}
                onToggleCollapse={toggleSynopsisCollapsed}
                headerAnchorId={AnchorIds.STORY_BIBLE_SYNOPSIS_HEADER}
                ctaShimmer={!!synopsisCtaShimmer}
                ctaEnabled={!!hasAccessToAgenticChat}
              />
              <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_SYNOPSIS_END} />

            </div>

            <div
              className="section chars"
              id="story-bible-characters"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-characters"
              ref={(ref) => (elementRefs.characters.current = ref)}
            >
              <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_CHARACTERS} />
              <Characters
                projectId={projectId}
                help={`This section affects: Outline, ${usesScenes ? "Scenes" : "Beats"}, and Prose`}
                collapsed={collapsedSections.characters}
                onToggleCollapse={toggleCharactersCollapsed}
                onSetCollapsed={setCharactersCollapsed}
              />

            </div>

            <div
              className="section worldbuilding"
              id="story-bible-worldbuilding"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-worldbuilding"
              ref={(ref) => (elementRefs.worldbuilding.current = ref)}
            >
              <Worldbuilding
                project={project}
                projectId={projectId}
                collapsed={collapsedSections.worldbuilding}
                onToggleCollapse={toggleWorldbuildingCollapsed}
                onSetCollapsed={setWorldbuildingCollapsed}
              />

            </div>

            <div
              className="section"
              id="story-bible-outline"
              tabIndex={-1}
              role="region"
              aria-labelledby="heading-outline"
              ref={(ref) => (elementRefs.outline.current = ref)}
            >
              <ProjectUpgradeOffer />
              {!displayLegacyOutline && (
                <StructuredOutline
                  projectId={projectId}
                  project={project}
                  documentId={documentId}
                  setDocumentId={setDocumentId}
                  collapsed={collapsedSections.outline}
                  onToggleCollapse={toggleOutlineCollapsed}
                />
              )}

            </div>
          </div>
        )}
        {/* Spacer */}
        <div
          style={{
            height: 500,
          }}
        />
      </div>

      {adminOpen && (
        <AdminMenu
          open={adminOpen}
          close={() => setAdminOpen(false)}
          setEngine={setEngine}
          engine={engine}
          invocations={invocations}
          setScenes={setScenes}
          scenes={scenes}
          setCharacters={setCharacters}
          setOutline={setOutline}
          setSynopsis={setSynopsis}
          setMetadata={setMetadata}
          setSeed={setSeed}
          setGenre={setGenre}
          setStyle={setStyle}
          stride={stride}
          setStride={setStride}
          currentStoryEngine={{ seed, characters, synopsis, outline, metadata, scenes }}
        />
      )}

      {outlinePickerOpen && (
        <OutlinePicker
          currentType={outlineType} //
          commitType={setOutlineType}
          close={(e) => {
            setOutlinePickerOpen(false);
          }}
        />
      )}

      {history && (
        <History
          storyEngineId={history.storyEngineId} //
          snapKey={history.key}
          id={history.id}
          user={user}
          title={history.title}
          close={() => setHistory(null)}
          onRestore={history.onRestore}
          loading={loading}
        />
      )}

      {loading && <div className="toast">Loading...</div>}
    </StyledContainer>
  );
}

export default React.memo(StoryBible);
